import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';

import { Button, Heading, Input, PopupSection, Popup, FormRow } from '@flixbus/honeycomb-react';

import { manageGroupDataStore } from './ManageGroupDataStore';

import styles from './styles.module.css';

const ManageGroupDataModal = observer(() => {
	const targetRef = useRef(null);

	const {
		isActive: createIsActive,
		deactivateCreationFlow,
		deactivateEditFlow,
		createNewGroup,
		updateGroup,
		nextFromStepBack,
		isLoading,
		isInCreationMode,
		isFromStepBack,
		groupData,
	} = manageGroupDataStore;

	const {
		control,
		getValues,
		handleSubmit,
		formState: { dirtyFields, isValid },
		reset,
	} = useForm({
		mode: 'onChange',
		defaultValues: groupData,
	});

	useEffect(() => {
		// Reset form if MobX store updates (optional but good practice)
		reset(groupData);
	}, [reset, groupData]);

	const handleConfirm = () => {
		if (!Object.keys(dirtyFields).length || (!isValid && isFromStepBack)) {
			nextFromStepBack();
		}
		const variables = getValues();
		if (isInCreationMode) {
			createNewGroup(variables);
		} else {
			updateGroup(variables);
		}
	};

	const handleReset = () => {
		if (isInCreationMode) {
			deactivateCreationFlow();
		} else {
			deactivateEditFlow();
		}
	};
	const heading = isInCreationMode || isFromStepBack ? 'New group' : 'Edit group';
	const label =
		isInCreationMode || isFromStepBack
			? 'Group creation is simple: first, name your group and add a description, then proceed to the next step to select crew members'
			: 'Modify the group details as needed';

	const buttonLabel = isInCreationMode || isFromStepBack ? 'Next' : 'Update';

	return (
		<Popup
			id="group-creation"
			aria-labelledby="group-creation"
			aria-describedby="group-creation"
			active={createIsActive}
			onOverlayClick={handleReset}
			targetRef={targetRef}
		>
			<PopupSection type="title">
				<Heading id="group-create-header" size={1} sectionHeader flushSpace>
					{heading}
				</Heading>
			</PopupSection>

			<PopupSection extraClasses={styles.antiScroll} type="content" id="creation">
				<div style={{ marginBottom: '12px' }}>{label}</div>

				<form id="createGroup" onSubmit={handleSubmit(handleConfirm)}>
					<FormRow>
						<Controller
							name="name"
							control={control}
							rules={{
								required: true,
							}}
							render={({ field, field: { value } }) => (
								<Input
									id="name"
									label="Group name"
									name="name"
									placeholder="E.g. 'My Group' "
									value={value}
									onChange={(e) => {
										field.onChange(e.target.value);
									}}
								/>
							)}
						/>
					</FormRow>
					<FormRow>
						<Controller
							name="description"
							control={control}
							rules={{
								required: false,
							}}
							render={({ field, field: { value } }) => (
								<Input
									id="description"
									label="Description"
									name="description"
									placeholder="E.g. 'My carefully selected crew members' "
									value={value}
									onChange={(e) => {
										field.onChange(e.target.value);
									}}
								/>
							)}
						/>
					</FormRow>
				</form>
			</PopupSection>
			<PopupSection type="actions">
				<Button onClick={handleReset}>Cancel</Button>
				<Button
					appearance="primary"
					onClick={() => handleConfirm()}
					disabled={(!Object.keys(dirtyFields).length || !isValid) && !isFromStepBack}
					loading={isLoading}
				>
					{buttonLabel}
				</Button>
			</PopupSection>
		</Popup>
	);
});

export default ManageGroupDataModal;
